import { Fragment } from 'react';
import { TableRow, TableCell, Box } from '@mui/material';
import { AnyPlan } from '../../types';
import OfflineBudgetHeader from './OfflineBudgetHeader/OfflineBudgetHeader';
import OfflineBudgetGRPTable from './OfflineBudgetGRPTable/OfflineBudgetGRPTable';
import {
  formatTouchpointLabel,
  getChildForEntry,
  getChildRowsForTouchpoint,
  getOfflineEntryForPlan,
  getOfflineTouchpoints,
} from './helpers';

type OfflineBudgetSectionProps = {
  country: string;
  plans: AnyPlan[];
  formatBudget: (v?: number, currency?: string) => string;
  firstColWidth?: number;
  showOnlyDifferences: boolean;
};

const TV_TOUCHPOINT_KEYS = new Set(['tv']);
const OOH_TOUCHPOINT_KEYS = new Set(['outdoor', 'ooh']);

export default function OfflineBudgetSection({
  country,
  plans,
  formatBudget,
  firstColWidth = 243,
  showOnlyDifferences,
}: OfflineBudgetSectionProps) {
  const touchpoints = getOfflineTouchpoints(plans, country);

  const hasAnyOffline = touchpoints.length > 0;
  if (showOnlyDifferences && !hasAnyOffline) return null;

  const offlineTotals = plans.map((plan) => {
    let sum = 0;
    let currency: string | undefined;

    touchpoints.forEach((tp) => {
      if (tp === 'tech cost') return;

      const entry = getOfflineEntryForPlan(plan, country, tp);
      const val = entry?.budget?.value;

      if (typeof val === 'number') {
        sum += val;
        if (!currency && entry?.budget?.currency)
          currency = entry.budget.currency;
      }
    });

    return { sum, currency };
  });

  const offlineTotalsSums = offlineTotals.map((t) => t.sum);

  return (
    <>
      <OfflineBudgetHeader
        plans={plans}
        firstColWidth={firstColWidth}
        formatBudget={formatBudget}
        offlineTotals={offlineTotals}
        offlineTotalsSums={offlineTotalsSums}
        showOnlyDifferences={showOnlyDifferences}
      />

      {touchpoints.map((tp) => {
        const label = formatTouchpointLabel(tp);

        const isTV = TV_TOUCHPOINT_KEYS.has(tp);
        const isOOH = OOH_TOUCHPOINT_KEYS.has(tp);

        const touchpointEntries = plans.map((plan) =>
          getOfflineEntryForPlan(plan, country, tp)
        );

        const tpBudgetValues = touchpointEntries.map((e) => e?.budget?.value);

        const tpHasDiff = (() => {
          const base = tpBudgetValues[0];
          for (let i = 1; i < tpBudgetValues.length; i += 1) {
            if (tpBudgetValues[i] !== base) return true;
          }
          return false;
        })();

        const childRows =
          isTV || isOOH ? getChildRowsForTouchpoint(plans, country, tp) : [];

        return (
          <Fragment key={tp}>
            {/* Touchpoint header */}
            {(!showOnlyDifferences || tpHasDiff) && (
              <TableRow
                sx={{
                  bgcolor: '#F5F5F5',
                  height: '52px',
                  border: '1px solid',
                  borderColor: '#C7D8F9',
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 700,
                    fontSize: '14px',
                    borderRight: '1px solid',
                    borderLeft: '1px solid',
                    borderColor: '#C7D8F9',
                  }}
                >
                  {label}
                </TableCell>

                {plans.map((plan, index) => {
                  const entry = touchpointEntries[index];
                  const budget = entry?.budget;
                  const value = budget?.value;

                  const diff =
                    index > 0 && tpBudgetValues[index] !== tpBudgetValues[0];

                  return (
                    <TableCell
                      key={`${tp}-parent-${plan.id}`}
                      sx={{
                        borderLeft: '1px solid',
                        borderColor: '#C7D8F9',
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: '14px',
                        color: diff ? 'error.main' : '#525F81',
                      }}
                    >
                      {typeof value === 'number'
                        ? formatBudget(value, budget?.currency)
                        : 'N/A'}
                    </TableCell>
                  );
                })}
              </TableRow>
            )}
            {/* OOH rows */}
            {isOOH &&
              childRows.map((child) => {
                const perPlan = plans.map((plan) => {
                  const entry = getOfflineEntryForPlan(plan, country, tp);
                  const ch = getChildForEntry(entry, child.key, tp);
                  return { entry, ch };
                });

                const rowValues = perPlan.map((d) => d.ch?.budget?.value);
                const rowCurrencies = perPlan.map(
                  (d) => d.ch?.budget?.currency
                );

                const hasDiff = (() => {
                  const base = rowValues[0];
                  for (let i = 1; i < rowValues.length; i += 1) {
                    if (rowValues[i] !== base) return true;
                  }
                  return false;
                })();

                if (showOnlyDifferences && !hasDiff) return null;

                return (
                  <TableRow
                    key={`ooh-${child.key}`}
                    sx={{
                      height: '44px',
                      borderLeft: '1px solid',
                      borderRight: '1px solid',
                      borderBottom: '1px solid',
                      borderColor: '#C7D8F9',
                      bgcolor: '#ffffff',
                    }}
                  >
                    <TableCell
                      title={child.name}
                      sx={{
                        fontWeight: 700,
                        fontSize: '12px',
                        borderLeft: '1px solid',
                        borderColor: '#C7D8F9',
                        padding: '6px 28px',
                        verticalAlign: 'middle',
                      }}
                    >
                      <Box
                        sx={{
                          height: '4em',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <Box
                          sx={{
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 3,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            lineHeight: '1.4em',
                            whiteSpace: 'normal',
                            cursor: 'help',
                          }}
                        >
                          {child.name}
                        </Box>
                      </Box>
                    </TableCell>

                    {plans.map((plan, idx) => {
                      const v = rowValues[idx];
                      const curr = rowCurrencies[idx];

                      const diff = idx > 0 && rowValues[idx] !== rowValues[0];

                      return (
                        <TableCell
                          key={`ooh-${child.key}-${plan.id}`}
                          sx={{
                            borderLeft: '1px solid',
                            borderColor: '#C7D8F9',
                            textAlign: 'center',
                            fontWeight: 500,
                            fontSize: '13px',
                            color: diff ? 'error.main' : '#525F81',
                          }}
                        >
                          {typeof v === 'number'
                            ? formatBudget(v, curr)
                            : 'N/A'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            {/* GRP table */}
            {isTV &&
              childRows.map((child) => {
                const perPlanData = plans.map((plan) => {
                  const entry = getOfflineEntryForPlan(plan, country, tp);
                  const ch = getChildForEntry(entry, child.key, tp); // copy.id
                  return { entry, ch };
                });

                const budgetValues = perPlanData.map((d) =>
                  d.ch ? d.ch.budget?.value ?? null : null
                );
                const budgetCurrencies = perPlanData.map((d) =>
                  d.ch ? d.ch.budget?.currency : undefined
                );
                const splitValues = perPlanData.map((d) =>
                  d.ch ? d.ch.split ?? null : null
                );

                const grpValues = perPlanData.map((d) =>
                  d.ch ? d.ch.grp ?? null : null
                );
                const weeksValues = perPlanData.map((d) =>
                  d.ch ? d.ch.weeks ?? null : null
                );

                return (
                  <OfflineBudgetGRPTable
                    key={child.key}
                    plans={plans}
                    copyName={child.name}
                    formatBudget={formatBudget}
                    showOnlyDifferences={showOnlyDifferences}
                    budgetValues={budgetValues}
                    budgetCurrencies={budgetCurrencies}
                    grpValues={grpValues}
                    splitValues={splitValues}
                    weeksValues={weeksValues}
                  />
                );
              })}
          </Fragment>
        );
      })}
    </>
  );
}
